import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Toaster } from '@/components/common/Toast/Toaster'

type NotificationItem = {
  id: number
  title: string
  body: string | null
  read: boolean
  created_at: string
  open_path: string
}

type Payload = {
  unread_count: number
  notifications: NotificationItem[]
}

const POLL_INTERVAL_MS = 30_000

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime()
  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 1) return 'たった今'
  if (minutes < 60) return `${minutes}分前`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}時間前`
  return `${Math.floor(hours / 24)}日前`
}

/**
 * 医師向け通知ベル (#1072)
 * 実装は本コンポーネント1ソースで、新ホーム（Reactレイアウト）と旧管理画面
 * （ERBヘッダーにReactの島としてマウント）の両方から使う。
 * /dashboard/notifications をポーリングし、新着があればバッジ更新＋トースト表示する。
 * standalone=true（ERB側マウント）のときは自前のToasterを併設する
 * （新ホーム側はPersistentLayoutのToasterを使うため二重にしない）。
 */
export default function NotificationBell({ standalone = false }: { standalone?: boolean }) {
  const [payload, setPayload] = useState<Payload | null>(null)
  const [open, setOpen] = useState(false)
  const knownIdsRef = useRef<Set<number> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const lastFetchedAtRef = useRef(0)
  const requestSeqRef = useRef(0)

  const fetchNotifications = useCallback(async (signal?: AbortSignal) => {
    const seq = ++requestSeqRef.current
    lastFetchedAtRef.current = Date.now()
    try {
      const res = await fetch('/dashboard/notifications', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal,
      })

      // セッションが切れるとログイン画面へリダイレクトされる。放置された端末に
      // 患者氏名を含む通知が残り続けないよう、表示中の内容を消す。ポーリングは
      // 止めない（再ログインすれば自然に復帰するため）
      if (res.redirected || res.status === 401 || res.status === 403) {
        knownIdsRef.current = null
        setPayload(null)
        return
      }
      if (!res.ok) return
      const data = (await res.json()) as Payload

      // unmount後や、追い越された古いレスポンスは捨てる。そうしないとログアウト後の
      // 画面にトーストが出たり、古い内容で新しい状態を上書きしたりする
      if (signal?.aborted || seq !== requestSeqRef.current) return

      // 初回ロード以降に増えた通知だけトーストで知らせる（診療中の新着に気づけるように）
      if (knownIdsRef.current) {
        const fresh = data.notifications.filter((n) => !knownIdsRef.current?.has(n.id))
        fresh.forEach((n) => {
          toast.info(n.title, {
            description: n.body ?? undefined,
            action: { label: '開く', onClick: () => { window.location.href = n.open_path } },
          })
        })
      }
      knownIdsRef.current = new Set(data.notifications.map((n) => n.id))
      setPayload(data)
    } catch {
      // ポーリングの一時的な失敗は無視する（次回のポーリングで回復する）
    }
  }, [])

  // タブが非表示のあいだはリクエストを出さない。医師が多数のタブを開いたまま放置しても
  // 流量が積み上がらないようにするため (#1183)。
  useEffect(() => {
    const controller = new AbortController()
    let timer: number | undefined

    // setInterval ではなく毎回次回を予約し直す。再表示時に取り直したときも間隔が
    // そこから測り直されるので、復帰直後に続けて2回叩いてしまうことがない
    const scheduleNext = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(poll, POLL_INTERVAL_MS)
    }

    function poll() {
      if (document.visibilityState === 'visible') void fetchNotifications(controller.signal)
      scheduleNext()
    }

    // 非表示中に溜まった通知へ、タブに戻った瞬間に気づけるようにする。ただしタブを
    // 往復されるたびに叩かないよう、直前の取得からポーリング間隔未満なら見送る
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - lastFetchedAtRef.current < POLL_INTERVAL_MS) return
      poll()
    }

    poll()
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      controller.abort()
    }
  }, [fetchNotifications])

  // ドロップダウン外クリックで閉じる
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [open])

  const markAllRead = async () => {
    await fetch('/dashboard/notifications/mark_all_read', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken() },
      credentials: 'same-origin',
    })
    void fetchNotifications()
  }

  const unreadCount = payload?.unread_count ?? 0

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        aria-label="通知"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 text-gray-500 rounded-lg hover:text-gray-900 hover:bg-gray-100 cursor-pointer"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[11px] font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 max-w-[90vw] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-900">お知らせ</span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="text-xs text-blue-600 hover:underline cursor-pointer"
              >
                すべて既読にする
              </button>
            )}
          </div>
          <ul className="max-h-96 overflow-y-auto divide-y divide-gray-100">
            {(payload?.notifications.length ?? 0) === 0 && (
              <li className="px-4 py-6 text-sm text-gray-500 text-center">お知らせはありません</li>
            )}
            {payload?.notifications.map((n) => (
              <li key={n.id}>
                <a href={n.open_path} className={`block px-4 py-3 hover:bg-gray-50 ${n.read ? '' : 'bg-blue-50'}`}>
                  <p className="text-sm text-gray-900">{n.title}</p>
                  {n.body && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>}
                  <p className="text-[11px] text-gray-400 mt-1">{relativeTime(n.created_at)}</p>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {standalone && <Toaster />}
    </div>
  )
}
